import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiFetch } from '../utils/api';
import { API_BASE_URL } from '../config';
import './BillOfLading.css';

interface BolItem {
  itemName: string;
  upc: string | null;
  quantity: number;
  weightLbs: number;
  kegCodes: string[];
}

interface BolSummary {
  packageType: string;
  quantity: number;
  weightLbs: number;
}

interface BolPayload {
  title: string;
  printDate: string;
  invoiceNumber: string;
  poNumber: string | null;
  taxType: string;
  pickupDate: string | null;
  shipper: { name: string; address: string | null; representativeLabel: string };
  shipTo: { name: string; address: string | null; licenseNumber: string | null };
  billTo: { name: string; address: string | null };
  items: BolItem[];
  packageSummary: BolSummary[];
  totalWeightLbs: number;
  includeKegSerials?: boolean;
  kegSerialCount?: number;
  signatures: { shipperRepresentative: string; customerRepresentative: string };
}

function fmtQty(n: number) {
  return Number(n).toFixed(2);
}

function fmtWeight(n: number) {
  return `${Number(n).toFixed(2)} Pound(s)`;
}

const BillOfLading: React.FC = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const [bol, setBol] = useState<BolPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await apiFetch(`${API_BASE_URL}/api/invoices/${invoiceId}/bol`, {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || `HTTP ${res.status}`);
        }
        setBol(await res.json());
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load Bill of Lading');
      }
    };
    load();
  }, [invoiceId]);

  if (error) {
    return (
      <div className="page-container bol-screen">
        <div className="bol-toolbar no-print">
          <button
            type="button"
            className="bol-btn-secondary"
            onClick={() => navigate(`/invoices/${invoiceId}`)}
            data-testid="bol-close"
          >
            Close
          </button>
        </div>
        <div className="bol-error">{error}</div>
      </div>
    );
  }

  if (!bol) return <div className="page-container bol-screen">Loading Bill of Lading…</div>;

  return (
    <div className="page-container bol-screen">
      <div className="bol-toolbar no-print">
        <button
          type="button"
          className="bol-btn-primary"
          onClick={() => {
            document.body.classList.add('bol-printing');
            const cleanup = () => {
              document.body.classList.remove('bol-printing');
              window.removeEventListener('afterprint', cleanup);
            };
            window.addEventListener('afterprint', cleanup);
            window.print();
          }}
          data-testid="bol-print"
        >
          Print BOL
        </button>
        <button
          type="button"
          className="bol-btn-secondary"
          onClick={() => navigate(`/invoices/${invoiceId}`)}
          data-testid="bol-close"
        >
          Close
        </button>
      </div>

      <article className="bol-page">
        <header className="bol-header">
          <div className="bol-shipper">
            <h1>BILL OF LADING</h1>
            <p className="bol-shipper-name">{bol.shipper.name}</p>
            {bol.shipper.address &&
              bol.shipper.address.split(',').map((line, i) => (
                <p key={i}>{line.trim()}</p>
              ))}
            <p className="bol-print-date">Print date: {bol.printDate}</p>
          </div>
          <div className="bol-meta">
            <p>
              <strong>Invoice:</strong> {bol.invoiceNumber}
            </p>
            <p>
              <strong>PO Number:</strong> {bol.poNumber || '—'}
            </p>
            <p>
              <strong>Tax Type:</strong> {bol.taxType}
            </p>
          </div>
        </header>

        <section className="bol-parties">
          <div>
            <h2>Ship To:</h2>
            <p className="bol-party-name">{bol.shipTo.name}</p>
            {bol.shipTo.address && <p style={{ whiteSpace: 'pre-line' }}>{bol.shipTo.address}</p>}
            <p>
              <strong>License Number:</strong> {bol.shipTo.licenseNumber || '—'}
            </p>
            <p>
              <strong>Delivery / Pickup Date:</strong> {bol.pickupDate || '—'}
            </p>
          </div>
          <div>
            <h2>Bill To:</h2>
            <p className="bol-party-name">{bol.billTo.name}</p>
            {bol.billTo.address && <p style={{ whiteSpace: 'pre-line' }}>{bol.billTo.address}</p>}
          </div>
        </section>

        <section>
          <h2>Item Details</h2>
          <table className="bol-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>UPC</th>
                <th className="num">Quantity</th>
                <th className="num">Weight</th>
              </tr>
            </thead>
            <tbody>
              {bol.items.map((item, idx) => (
                <tr key={idx}>
                  <td>
                    {item.itemName}
                    {bol.includeKegSerials && item.kegCodes?.length > 0 && (
                      <div className="bol-keg-codes">
                        Keg serials: {item.kegCodes.join(', ')}
                      </div>
                    )}
                  </td>
                  <td>{item.upc || ''}</td>
                  <td className="num">{fmtQty(item.quantity)}</td>
                  <td className="num">{fmtWeight(item.weightLbs)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section>
          <h2>Package Type Summary</h2>
          <table className="bol-table">
            <thead>
              <tr>
                <th>Package Type</th>
                <th className="num">Quantity</th>
                <th className="num">Weight</th>
              </tr>
            </thead>
            <tbody>
              {bol.packageSummary.map((row) => (
                <tr key={row.packageType}>
                  <td>{row.packageType}</td>
                  <td className="num">{fmtQty(row.quantity)}</td>
                  <td className="num">{fmtWeight(row.weightLbs)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="bol-total-weight">TOTAL WEIGHT: {fmtWeight(bol.totalWeightLbs)}</p>
        </section>

        <section className="bol-signatures">
          <div className="bol-sig-block">
            <div className="bol-sig-line" />
            <p>{bol.signatures.shipperRepresentative}</p>
          </div>
          <div className="bol-sig-block">
            <div className="bol-sig-line" />
            <p>{bol.signatures.customerRepresentative}</p>
          </div>
          <div className="bol-sig-block">
            <div className="bol-sig-line" />
            <p>Driver Name</p>
          </div>
          <div className="bol-sig-block">
            <div className="bol-sig-line" />
            <p>Date</p>
          </div>
        </section>
      </article>
    </div>
  );
};

export default BillOfLading;
